import {
  ChevronRight,
  LogOut,
  Monitor,
  Moon,
  Sun
} from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { getDrawerSections } from '@/app/shell/navigationManifest';
import { Logo } from '@/components/branding/Logo';
import { MenuToggleButton } from '@/components/navigation/MenuToggleButton';
import { Button } from '@/components/ui/Button';
import { appInfo } from '@/config/appInfo';
import { logout } from '@/features/auth/store/authSlice';
import { useClinicProfile } from '@/features/owner/api/ownerApi';
import { PwaInstallButton } from '@/features/pwa/components/PwaInstallButton';
import { closeSideDrawer, toggleTheme } from '@/features/ui/store/uiSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { routePaths } from '@/routes/paths';
import { cn } from '@/utils/cn';

export function SideDrawer() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(state => state.ui.isSideDrawerOpen);
  const theme = useAppSelector(state => state.ui.theme);
  const userName = useAppSelector(state => state.auth.userName);
  const userEmail = useAppSelector(state => state.auth.userEmail);
  const permissions = useAppSelector(state => state.auth.permissions);
  const location = useLocation();
  const clinicProfileQuery = useClinicProfile(isOpen);
  const sections = getDrawerSections(permissions);
  const brandProductName = clinicProfileQuery.data?.brandProductName ?? 'AarogyaFlow';
  const clinicName = clinicProfileQuery.data?.clinicName ?? 'Clinic workspace';
  const logoUrl = clinicProfileQuery.data?.logoUrl ?? undefined;

  const closeDrawer = useCallback(() => {
    dispatch(closeSideDrawer());
  }, [dispatch]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [closeDrawer, isOpen]);

  return (
    <div
      aria-hidden={!isOpen}
      className={cn(
        'fixed inset-0 z-40 transition duration-250 ease-out lg:hidden',
        isOpen
          ? 'pointer-events-auto bg-slate-950/38 backdrop-blur-[2px] dark:bg-slate-950/72'
          : 'pointer-events-none bg-black/0'
      )}
      onClick={closeDrawer}
    >
      <aside
        className={cn(
          'ml-auto flex h-full w-[84%] max-w-[336px] flex-col border-l border-slate-200/80 bg-slate-50 shadow-[0_28px_60px_-30px_rgba(15,23,42,0.38)] transition duration-300 ease-out dark:border-slate-800 dark:bg-slate-950',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={event => event.stopPropagation()}
      >
        <div className="border-b border-slate-200/80 px-4 pb-4 pt-5 dark:border-slate-800">
          <div className="flex items-start justify-between gap-3">
            <Link
              to={routePaths.app.dashboard}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-[1.5rem] border border-slate-200/80 bg-white/90 px-3 py-3 shadow-[0_16px_32px_-28px_rgba(15,23,42,0.28)] transition hover:bg-white dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-900"
              onClick={closeDrawer}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 shadow-inner dark:bg-slate-800">
                <Logo variant="icon" imgSrc={logoUrl} className="h-8 w-8 shrink-0" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold tracking-tight text-slate-900 dark:text-white">
                  {brandProductName}
                </p>
                <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{clinicName}</p>
              </div>
            </Link>

            <MenuToggleButton
              isOpen={isOpen}
              compact
              className="mt-1 shrink-0 border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              onClick={closeDrawer}
            />
          </div>
        </div>

        <div className="drawer-safe flex-1 space-y-5 overflow-y-auto p-4 no-scrollbar">
          {sections.map(section => (
            <div key={section.key}>
              <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                {section.label}
              </p>
              <div className="space-y-1.5 rounded-[1.75rem] border border-slate-200/80 bg-white/95 p-2 shadow-[0_20px_40px_-34px_rgba(15,23,42,0.22)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_18px_36px_-30px_rgba(2,6,23,0.82)]">
                {section.items.map(item => {
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.key}
                      to={item.path}
                      className={cn(
                        'flex min-h-12 items-center gap-3 rounded-[1.2rem] px-3 py-2.5 text-sm font-medium transition',
                        isActive
                          ? 'bg-gradient-to-r from-brand-50 to-brand-100/80 text-brand-800 shadow-[0_18px_30px_-24px_rgba(37,99,235,0.42)] dark:from-brand-500/20 dark:to-brand-400/10 dark:text-brand-100'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                      )}
                      onClick={closeDrawer}
                    >
                      <span
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl transition',
                          isActive
                            ? 'bg-white/80 text-brand-700 dark:bg-slate-950/40 dark:text-brand-100'
                            : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300'
                        )}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{item.title}</span>
                        {item.description ? (
                          <span className="block truncate text-[11px] font-medium text-slate-500 dark:text-slate-400">
                            {item.description}
                          </span>
                        ) : null}
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}

          <PwaInstallButton variant="menu" />

          <button
            type="button"
            className="flex min-h-12 w-full items-center justify-between rounded-[1.5rem] border border-slate-200/80 bg-white px-3.5 py-3 text-left text-sm font-medium text-slate-700 shadow-[0_14px_28px_-26px_rgba(15,23,42,0.18)] transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={() => dispatch(toggleTheme())}
          >
            <span className="inline-flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                {theme === 'system' ? (
                  <Monitor className="h-4 w-4" />
                ) : theme === 'dark' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </span>
              Theme
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {theme}
            </span>
          </button>
        </div>

        <div className="border-t border-slate-200/80 p-4 dark:border-slate-800">
          <div className="mb-3 rounded-[1.6rem] border border-slate-200/80 bg-white px-3.5 py-3 text-xs text-slate-600 shadow-[0_18px_34px_-30px_rgba(15,23,42,0.24)] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {(userName ?? 'U').trim().charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900 dark:text-white">{userName ?? 'Logged in user'}</p>
                <p className="truncate text-slate-500 dark:text-slate-400">{userEmail ?? 'No email available'}</p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.14em] text-slate-400">v{appInfo.version}</p>
              </div>
            </div>
          </div>
          <Button
            className="w-full rounded-[1.35rem] bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            onClick={() => {
              dispatch(logout());
              closeDrawer();
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </aside>
    </div>
  );
}
